import type { Biller, FeatureFlag } from '@contract';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { MoneyChannels } from '@/components/money-channels';
import { PageHeader } from '@/components/page-header';
import { ProposeChange } from '@/components/propose-change';
import { ReasonDialog } from '@/components/reason-dialog';
import { StatusBadge } from '@/components/record-table';
import { SettingsPanel } from '@/components/settings-panel';
import { TabLinks } from '@/components/tab-links';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TablePager, usePaging } from '@/components/ui/table-pager';
import { callAdmin } from '@/lib/api';
import { adminQuery, invalidate } from '@/lib/query';
import {
  filterSettings,
  isStaffVisibleKey,
  SETTING_TABS,
  type SettingTab,
  settingTabOf,
} from '@/lib/settings-meta';
import { useCan, useStaff } from '@/lib/staff-context';
import { toast } from '@/lib/toast';

const TAB_KEYS = [
  ...SETTING_TABS.map((item) => item.key),
  'flags',
  'bills',
] as const;

type Tab = (typeof TAB_KEYS)[number];

const searchSchema = z.object({
  tab: z.enum(TAB_KEYS).catch('channels').default('channels'),
});

export const Route = createFileRoute('/_app/settings')({
  validateSearch: searchSchema,
  component: SettingsPage,
});

/** Runtime settings by group, feature flags and the billers customers see. */
function SettingsPage() {
  const { tab } = Route.useSearch();
  const [query, setQuery] = useState('');
  const list = useQuery(adminQuery('settings.list', {}));
  const needle = query.trim();
  const counts = new Map<string, number>();
  if (needle && list.data) {
    for (const row of filterSettings(list.data, needle)) {
      if (!isStaffVisibleKey(row.key)) continue;
      const key = settingTabOf(row.key);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  const tabs = [
    ...SETTING_TABS.filter(
      (item) => item.key !== 'other' || list.data?.some(isOther) !== false,
    ),
    { key: 'flags', label: 'Feature flags' },
    { key: 'bills', label: 'Billers' },
  ].map((item) => ({
    ...item,
    label: counts.has(item.key)
      ? `${item.label} (${counts.get(item.key)})`
      : item.label,
  }));
  return (
    <>
      <PageHeader
        title="Settings"
        description="What the app and the server read while they run."
      />
      {tab === 'flags' || tab === 'bills' ? null : (
        <Input
          type="search"
          aria-label="Search settings"
          placeholder="Search settings"
          className="sm:max-w-sm"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      )}
      <TabLinks
        tabs={tabs}
        active={tab}
        render={(item, domProps) => (
          <Link
            {...domProps}
            to="/settings"
            search={{ tab: item.key as Tab }}
          />
        )}
      />
      {tab === 'flags' ? <Flags /> : null}
      {tab === 'bills' ? <Billers /> : null}
      {tab === 'channels' ? <MoneyChannels query={query} /> : null}
      {tab === 'flags' || tab === 'bills' || tab === 'channels' ? null : (
        <SettingsPanel tab={tab as SettingTab} query={query} />
      )}
    </>
  );
}

function isOther(row: { key: string }): boolean {
  return isStaffVisibleKey(row.key) && settingTabOf(row.key) === 'other';
}

// ---------------------------------------------------------------------------
// Feature flags
// ---------------------------------------------------------------------------

const ENVIRONMENTS = ['development', 'preview', 'production'] as const;

function Flags() {
  const list = useQuery(adminQuery('flags.list', {}));
  const flags = list.data?.filter((flag) => isStaffVisibleKey(flag.key));
  const paging = usePaging(flags);
  if (list.isError) {
    return <Alert variant="destructive">{list.error.message}</Alert>;
  }
  return (
    <>
      <p className="text-sm text-muted-foreground">
        Development and preview change at once and are logged with your reason.
        Production is a change request, except for the super admin, who owns the
        flags and changes them at once.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Flag</TableHead>
            {ENVIRONMENTS.map((env) => (
              <TableHead key={env} className="capitalize">
                {env}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {paging.visible?.map((flag) => (
            <TableRow key={flag.key}>
              <TableCell>
                <div className="font-mono text-xs">{flag.key}</div>
                <div className="text-xs text-muted-foreground">
                  {flag.description}
                </div>
              </TableCell>
              {ENVIRONMENTS.map((env) => (
                <TableCell key={env}>
                  <FlagCell flag={flag} environment={env} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePager paging={paging} />
    </>
  );
}

function FlagCell({
  flag,
  environment,
}: Readonly<{
  flag: FeatureFlag;
  environment: (typeof ENVIRONMENTS)[number];
}>) {
  const state = flag.environments[environment];
  const enabled = Boolean(state?.enabled);
  const label = enabled ? 'Turn off' : 'Turn on';
  // The super admin owns the flags: production changes at once for them,
  // and goes through a change request for everyone else.
  const staff = useStaff();
  return (
    <div className="flex flex-col items-start gap-1">
      <StatusBadge status={enabled ? 'on' : 'off'} />
      {environment === 'production' && !staff.is_super_admin ? (
        <ProposeChange
          kind="feature_flag"
          payload={{ key: flag.key, enabled: !enabled }}
          label={label}
          title={`${label} ${flag.key} in production`}
          description="Every customer's app reads it on its next refresh."
        />
      ) : (
        <FlagSwitch
          flag={flag}
          environment={environment}
          enabled={enabled}
          label={label}
        />
      )}
    </div>
  );
}

function FlagSwitch({
  flag,
  environment,
  enabled,
  label,
}: Readonly<{
  flag: FeatureFlag;
  environment: (typeof ENVIRONMENTS)[number];
  enabled: boolean;
  label: string;
}>) {
  const [open, setOpen] = useState(false);
  const set = useMutation({
    mutationFn: (reason: string) =>
      callAdmin('flags.set', {
        key: flag.key,
        environment,
        enabled: !enabled,
        reason,
      }),
    onSuccess: async () => {
      await invalidate('flags.list');
      setOpen(false);
      toast.success(
        `${flag.key} is ${enabled ? 'off' : 'on'} in ${environment}.`,
      );
    },
  });
  if (!useCan('flags.set')) return null;
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        {label}
      </Button>
      <ReasonDialog
        open={open}
        onOpenChange={setOpen}
        title={`${label} ${flag.key} in ${environment}`}
        description={
          environment === 'production'
            ? "Takes effect at once. Every customer's app reads it on its next refresh."
            : 'Only test builds read this environment.'
        }
        confirmLabel={label}
        pending={set.isPending}
        error={set.error?.message ?? null}
        onConfirm={(reason) => set.mutate(reason)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Billers
// ---------------------------------------------------------------------------

function Billers() {
  const list = useQuery(adminQuery('bills.catalog', {}));
  const paging = usePaging(list.data);
  if (list.isError) {
    return <Alert variant="destructive">{list.error.message}</Alert>;
  }
  return (
    <>
      <p className="text-sm text-muted-foreground">
        A hidden biller leaves the app's Pay bills list and refuses new
        payments. Payments already sent finish as usual.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Biller</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>In the app</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {paging.visible?.map((biller) => (
            <BillerRow key={biller.id} biller={biller} />
          ))}
        </TableBody>
      </Table>
      <TablePager paging={paging} />
    </>
  );
}

function BillerRow({ biller }: Readonly<{ biller: Biller }>) {
  const label = biller.hidden ? 'Show' : 'Hide';
  return (
    <TableRow>
      <TableCell>
        <div>{biller.name}</div>
        <div className="font-mono text-xs text-muted-foreground">
          {biller.id}
        </div>
      </TableCell>
      <TableCell className="capitalize">{biller.category}</TableCell>
      <TableCell>
        <StatusBadge status={biller.hidden ? 'hidden' : 'shown'} />
      </TableCell>
      <TableCell>
        <ProposeChange
          kind="biller_visibility"
          payload={{ biller_id: biller.id, hidden: !biller.hidden }}
          label={label}
          title={`${label} ${biller.name}`}
          description={
            biller.hidden
              ? 'Customers can pay this biller again.'
              : 'Customers stop seeing this biller.'
          }
        />
      </TableCell>
    </TableRow>
  );
}
